import React, { useEffect, useState } from 'react';
import { 
  Building2, CalendarClock, 
  Home, Landmark, MapPin, 
  FileText, DollarSign, 
  GraduationCap, School, 
  Hospital, TreePine, 
  ShoppingBag, Bus, Shield,
  Wifi, Droplet, Lightbulb
} from 'lucide-react';
import { propertyLabels } from './publishes/InfPublish';

// Configuración para lugares cercanos (importada de LugPublish.jsx)
const lugaresConfig = [
  { id: 'universidades', Icon: GraduationCap, label: 'Universidades' },
  { id: 'colegios', Icon: School, label: 'Colegios' },
  { id: 'hospitales', Icon: Hospital, label: 'Hospitales' },
  { id: 'parques', Icon: TreePine, label: 'Parques' },
  { id: 'centros_comerciales', Icon: ShoppingBag, label: 'Centros Comerciales' },
  { id: 'transporte_publico', Icon: Bus, label: 'Transporte Público' }
];

// Configuración para servicios (similar a LugPublish.jsx)
const serviciosConfig = [
  { id: 'agua', Icon: Droplet, label: 'Agua' },
  { id: 'luz', Icon: Lightbulb, label: 'Luz' },
  { id: 'desague', Icon: Wifi, label: 'Desagüe' },
  { id: 'internet', Icon: Wifi, label: 'Internet' },
  { id: 'seguridad', Icon: Shield, label: 'Seguridad' }
];

const proyectoEstados = [
  { id: 'planos', label: 'En Planos' },
  { id: 'lanzamiento', label: 'Lanzamiento' },
  { id: 'construccion', label: 'En Construcción' },
  { id: 'terminado', label: 'Terminado' }
];

const proyectoTipos = [
  { id: 'residencial', label: 'Residencial' },
  { id: 'comercial', label: 'Comercial' },
  { id: 'mixto', label: 'Mixto' }
];

const mesesOptions = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Generar años desde el actual hasta 10 años en el futuro
const currentYear = new Date().getFullYear();
const yearsOptions = Array.from({ length: 10 }, (_, i) => currentYear + i);

const bancosOptions = [
  'BCP', 'BBVA', 'Interbank', 'Scotiabank', 'Banco de la Nación',
  'Banco Pichincha', 'Banco GNB', 'Banco Falabella', 'Mi Banco', 'Otro'
];

const ConsPublish = ({ 
  formData = {}, 
  handleInputChange, 
  setFormData,
  handleCurrencyChange,
  isAutoConverting,
  errors
}) => {
  // Estado local para manejar el renderizado mientras se inicializa formData
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeTab, setActiveTab] = useState('informacion'); // 'informacion', 'lugares', 'servicios'
  
  // Inicializar los campos del proyecto si no existen
  useEffect(() => {
    if (setFormData) {
      setFormData(prev => {
        const updatedData = {
          ...prev,
          nombre_proyecto: prev.nombre_proyecto || '',
          descripcion_proyecto: prev.descripcion_proyecto || '',
          tipo_proyecto: prev.tipo_proyecto || '',
          estado_proyecto: prev.estado_proyecto || '',
          mes_entrega: prev.mes_entrega || '',
          anio_entrega: prev.anio_entrega || '',
          total_unidades: prev.total_unidades || '',
          tiene_1dorm: prev.tiene_1dorm || false,
          tiene_2dorm: prev.tiene_2dorm || false,
          tiene_3dorm: prev.tiene_3dorm || false,
          tiene_4dorm: prev.tiene_4dorm || false,
          tiene_financiamiento: prev.tiene_financiamiento || 'No',
          bancos_financiamiento: prev.bancos_financiamiento || [],
          amenidades: prev.amenidades || [],
          lugares_cercanos: prev.lugares_cercanos || [],
          servicios_basicos: prev.servicios_basicos || []
        };
        setIsInitialized(true);
        return updatedData;
      });
    }
  }, [setFormData]);
  
  // Crear un objeto seguro con valores por defecto
  const safeFormData = {
    nombre_proyecto: formData?.nombre_proyecto || '',
    descripcion_proyecto: formData?.descripcion_proyecto || '',
    tipo_proyecto: formData?.tipo_proyecto || '',
    estado_proyecto: formData?.estado_proyecto || '',
    mes_entrega: formData?.mes_entrega || '',
    anio_entrega: formData?.anio_entrega || '',
    total_unidades: formData?.total_unidades || '',
    tiene_1dorm: formData?.tiene_1dorm || false,
    tiene_2dorm: formData?.tiene_2dorm || false,
    tiene_3dorm: formData?.tiene_3dorm || false,
    tiene_4dorm: formData?.tiene_4dorm || false,
    tiene_financiamiento: formData?.tiene_financiamiento || 'No',
    bancos_financiamiento: formData?.bancos_financiamiento || [],
    amenidades: formData?.amenidades || [],
    lugares_cercanos: formData?.lugares_cercanos || [],
    servicios_basicos: formData?.servicios_basicos || [],
    otro_banco: formData?.otro_banco || '',
    detalles_financiamiento: formData?.detalles_financiamiento || '',
    otras_caracteristicas: formData?.otras_caracteristicas || '',
    ...formData
  };

  // Componente Tab para la navegación entre secciones
  const Tab = ({ id, label, icon: Icon, active }) => (
    <button
      type="button"
      onClick={() => setActiveTab(id)}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all
                ${active ? 'bg-blue-500 text-white' : 'hover:bg-blue-50'}`}
    >
      <Icon className="w-5 h-5" />
      <span>{label}</span>
    </button>
  );
  
  // Agregar verificación de renderizado condicional si es necesario
  if (!formData && !isInitialized) {
    return <div className="p-4 text-center">Cargando información del proyecto...</div>;
  }
  
  return (
    <div className="space-y-6">
      {/* Tabs de navegación */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Tab 
          id="informacion" 
          label="Información" 
          icon={Building2} 
          active={activeTab === 'informacion'} 
        />
        <Tab 
          id="lugares" 
          label="Lugares Cercanos" 
          icon={MapPin} 
          active={activeTab === 'lugares'} 
        />
        <Tab 
          id="servicios" 
          label="Servicios" 
          icon={Shield} 
          active={activeTab === 'servicios'} 
        />
      </div>

      {/* Sección de Información del Proyecto */}
      {activeTab === 'informacion' && (
        <>
          {/* Información del Proyecto */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-blue-500" />
              Información del Proyecto
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Nombre del Proyecto* 
                  <span className="text-gray-500 text-sm ml-2">
                    ({safeFormData.nombre_proyecto?.length || 0}/60 caracteres)
                  </span>
                </label>
                <input
                  type="text"
                  name="nombre_proyecto"
                  value={safeFormData.nombre_proyecto}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ej: Residencial Las Palmeras"
                  maxLength={60}
                />
                {errors?.nombre_proyecto && <p className="text-red-500 text-sm mt-1">{errors.nombre_proyecto}</p>}
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium mb-1">Estado del Proyecto*</label>
                  <select
                    name="estado_proyecto"
                    value={safeFormData.estado_proyecto}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccione estado</option>
                    {proyectoEstados.map(estado => (
                      <option key={estado.id} value={estado.id}>{estado.label}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-1">Tipo de Proyecto*</label>
                  <select
                    name="tipo_proyecto"
                    value={safeFormData.tipo_proyecto}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Seleccione tipo</option>
                    {proyectoTipos.map(tipo => (
                      <option key={tipo.id} value={tipo.id}>{tipo.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">
                Descripción del Proyecto* 
                <span className="text-gray-500 text-sm ml-2">
                  ({safeFormData.descripcion_proyecto?.length || 0}/700 caracteres)
                </span>
              </label>
              <textarea
                name="descripcion_proyecto"
                value={safeFormData.descripcion_proyecto}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg min-h-[120px] focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describa el concepto del proyecto, características principales y su posicionamiento..."
                maxLength={700}
              />
              {safeFormData.descripcion_proyecto?.length < 100 && safeFormData.descripcion_proyecto?.length > 0 && (
                <p className="text-amber-500 text-sm mt-1">
                  Se recomienda una descripción de al menos 100 caracteres
                </p>
              )}
            </div>
          </div>

          {/* Fecha de Entrega y Unidades */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2 flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-indigo-500" />
              Fecha de Entrega y Unidades
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Mes de Entrega Estimado*</label>
                <select
                  name="mes_entrega"
                  value={safeFormData.mes_entrega}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Seleccione mes</option>
                  {mesesOptions.map((mes, index) => (
                    <option key={index} value={mes}>{mes}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Año de Entrega Estimado*</label>
                <select
                  name="anio_entrega"
                  value={safeFormData.anio_entrega}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">Seleccione año</option>
                  {yearsOptions.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Total de Unidades*</label>
                <input
                  type="number"
                  name="total_unidades"
                  value={safeFormData.total_unidades}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Ej: 24"
                  min="1"
                />
              </div>
            </div>
            
            <div className="mt-6">
              <h4 className="text-md font-medium mb-3 flex items-center gap-2">
                <Home className="h-4 w-4" />
                Tipos de Unidades Disponibles
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4].map(num => (
                  <div key={num} className="border p-4 rounded-lg bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <label className="font-medium text-sm">Unidades de {num} Dormitorio{num > 1 ? 's' : ''}</label>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          checked={safeFormData[`tiene_${num}dorm`] || false}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              [`tiene_${num}dorm`]: e.target.checked,
                              ...(e.target.checked ? {} : {
                                [`unidades_${num}dorm`]: '',
                                [`area_${num}dorm`]: '',
                                [`precio_${num}dorm_soles`]: '',
                                [`precio_${num}dorm_dolares`]: ''
                              })
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-xs text-gray-500">Disponible</span>
                      </div>
                    </div>
                    
                    {safeFormData[`tiene_${num}dorm`] && (
                      <div className="space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs font-medium">Cantidad</label>
                            <input
                              type="number"
                              name={`unidades_${num}dorm`}
                              value={safeFormData[`unidades_${num}dorm`] || ''}
                              onChange={handleInputChange}
                              className="w-full p-1 border rounded text-sm"
                              placeholder="0"
                              min="1"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium">Área (m²)</label>
                            <input
                              type="number"
                              name={`area_${num}dorm`}
                              value={safeFormData[`area_${num}dorm`] || ''}
                              onChange={handleInputChange}
                              className="w-full p-1 border rounded text-sm"
                              placeholder="0"
                              min="1"
                            />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs font-medium">Precio (S/)</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none">
                              <span className="text-gray-500 text-xs">S/</span>
                            </div>
                            <input
                              type="number"
                              name={`precio_${num}dorm_soles`}
                              value={safeFormData[`precio_${num}dorm_soles`] || ''}
                              onChange={(e) => {
                                handleInputChange(e);
                                // Auto-convert to dollars
                                const solesValue = parseFloat(e.target.value);
                                if (!isNaN(solesValue)) {
                                  const dolaresValue = Math.round(solesValue / 3.80);
                                  setFormData(prev => ({
                                    ...prev,
                                    [`precio_${num}dorm_dolares`]: dolaresValue.toString()
                                  }));
                                }
                              }}
                              className="w-full pl-7 p-1 border rounded text-sm"
                              placeholder="0"
                              min="0"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Financiamiento */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2 flex items-center gap-2">
              <Landmark className="h-5 w-5 text-emerald-500" />
              Financiamiento
            </h3>
            
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">¿Ofrece Financiamiento Bancario?</label>
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, tiene_financiamiento: "Si" }))}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center gap-2 justify-center
                    ${safeFormData.tiene_financiamiento === "Si"
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-300'}`}
                >
                  <Landmark 
                    className={`h-5 w-5 ${
                      safeFormData.tiene_financiamiento === "Si" 
                        ? 'text-emerald-500' 
                        : 'text-gray-400'
                    }`} 
                  />
                  <span className="text-sm font-medium">Con Financiamiento</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    tiene_financiamiento: "No",
                    bancos_financiamiento: [] 
                  }))}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all flex items-center gap-2 justify-center
                    ${safeFormData.tiene_financiamiento === "No"
                      ? 'border-emerald-500 bg-emerald-50'
                      : 'border-gray-200 hover:border-emerald-300'}`}
                >
                  <div className="relative">
                    <Landmark className={`h-5 w-5 ${
                      safeFormData.tiene_financiamiento === "No" 
                        ? 'text-emerald-500' 
                        : 'text-gray-400'
                    }`} />
                    {safeFormData.tiene_financiamiento === "No" && (
                      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                        <div className="w-px h-6 bg-emerald-500 transform rotate-45"></div>
                      </div>
                    )}
                  </div>
                  <span className="text-sm font-medium">Sin Financiamiento</span>
                </button>
              </div>
            </div>
            
            {safeFormData.tiene_financiamiento === "Si" && (
              <div>
                <label className="block text-sm font-medium mb-2">Seleccione los bancos que financian este proyecto</label>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {bancosOptions.map((banco) => (
                    <button
                      key={banco}
                      type="button"
                      onClick={() => {
                        setFormData(prev => {
                          const bancos = prev.bancos_financiamiento || [];
                          return {
                            ...prev,
                            bancos_financiamiento: bancos.includes(banco)
                              ? bancos.filter(b => b !== banco)
                              : [...bancos, banco]
                          };
                        });
                      }}
                      className={`p-2 rounded-lg border transition-all text-center
                        ${(safeFormData.bancos_financiamiento || []).includes(banco)
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                          : 'border-gray-200 hover:border-emerald-300 text-gray-600'}`}
                    >
                      <span className="text-sm">{banco}</span>
                    </button>
                  ))}
                </div>
                
                {safeFormData.bancos_financiamiento?.includes('Otro') && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">Especifique otro banco</label>
                    <input
                      type="text"
                      name="otro_banco"
                      value={safeFormData.otro_banco}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Nombre del banco"
                    />
                  </div>
                )}
                
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-1">Detalles del financiamiento (opcional)</label>
                  <textarea
                    name="detalles_financiamiento"
                    value={safeFormData.detalles_financiamiento}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Ej: Requisitos, plazos, tasas preferenciales, etc."
                    rows="3"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Características y Amenidades */}
          <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
            <h3 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">Características y Amenidades</h3>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {['Piscina', 'Gimnasio', 'Áreas Verdes', 'Sala Común', 'Terraza', 'Estacionamiento', 'Seguridad 24/7', 'Juegos Infantiles', 'Zonas de BBQ'].map((amenidad) => (
                <button
                  key={amenidad}
                  type="button"
                  onClick={() => {
                    setFormData(prev => {
                      const amenidades = prev.amenidades || [];
                      return {
                        ...prev,
                        amenidades: amenidades.includes(amenidad)
                          ? amenidades.filter(a => a !== amenidad)
                          : [...amenidades, amenidad]
                      };
                    });
                  }}
                  className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2
                    ${(safeFormData.amenidades || []).includes(amenidad)
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'}`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${(safeFormData.amenidades || []).includes(amenidad) ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                    {(safeFormData.amenidades || []).includes(amenidad) && (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm font-medium">{amenidad}</span>
                </button>
              ))}
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1">Otras características (opcional)</label>
              <textarea
                name="otras_caracteristicas"
                value={safeFormData.otras_caracteristicas}
                onChange={handleInputChange}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                placeholder="Mencione otras características especiales del proyecto..."
                rows="2"
              />
            </div>
          </div>
        </>
      )}

      {/* Sección de Lugares Cercanos (de LugPublish.jsx) */}
      {activeTab === 'lugares' && (
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
          <h3 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">
            Lugares Cercanos
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {lugaresConfig.map(({ id, Icon, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => {
                  setFormData(prev => {
                    const lugares = prev.lugares_cercanos || [];
                    return {
                      ...prev,
                      lugares_cercanos: lugares.includes(id)
                        ? lugares.filter(l => l !== id)
                        : [...lugares, id]
                    };
                  });
                }}
                className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2
                  ${(safeFormData.lugares_cercanos || []).includes(id)
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'}`}
              >
                <Icon 
                  className={`h-6 w-6 ${
                    (safeFormData.lugares_cercanos || []).includes(id) 
                      ? 'text-blue-500' 
                      : 'text-gray-400'
                  }`} 
                />
                <span className="text-sm font-medium">{label}</span>
              </button>
            ))}
          </div>
          
          <div className="mt-6">
            <p className="text-sm text-gray-500">
              Seleccione los lugares cercanos al proyecto para que los compradores potenciales puedan conocer mejor la ubicación.
            </p>
          </div>
        </div>
      )}

     {/* Sección de Servicios Disponibles */}
{activeTab === 'servicios' && (
  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
    <h3 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">
      Servicios Disponibles
    </h3>
    
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {serviciosConfig.map(({ id, Icon, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => {
            setFormData(prev => {
              const servicios = prev.servicios_basicos || [];
              return {
                ...prev,
                servicios_basicos: servicios.includes(id)
                  ? servicios.filter(s => s !== id)
                  : [...servicios, id]
              };
            });
          }}
          className={`p-4 rounded-lg border-2 transition-all flex flex-col items-center gap-2
            ${(safeFormData.servicios_basicos || []).includes(id)
              ? 'border-green-500 bg-green-50'
              : 'border-gray-200 hover:border-green-300'}`}
        >
          <Icon 
            className={`h-6 w-6 ${
              (safeFormData.servicios_basicos || []).includes(id) 
                ? 'text-green-500' 
                : 'text-gray-400'
            }`} 
          />
          <span className="text-sm font-medium">{label}</span>
        </button>
      ))}
    </div>
    
    <div className="mt-6">
      <p className="text-sm text-gray-500">
        Seleccione los servicios básicos que estarán disponibles en el proyecto. Esto ayudará a los compradores a entender mejor las comodidades que ofrecerá.
      </p>
    </div>
  </div>
)}

{/* Pie de página con indicador de navegación */}
<div className="flex justify-center mt-8">
  <div className="inline-flex space-x-2 items-center">
    <div 
      className={`w-3 h-3 rounded-full ${activeTab === 'informacion' ? 'bg-blue-500' : 'bg-gray-300'}`}
      onClick={() => setActiveTab('informacion')}
    ></div>
    <div 
      className={`w-3 h-3 rounded-full ${activeTab === 'lugares' ? 'bg-blue-500' : 'bg-gray-300'}`}
      onClick={() => setActiveTab('lugares')}
    ></div>
    <div 
      className={`w-3 h-3 rounded-full ${activeTab === 'servicios' ? 'bg-blue-500' : 'bg-gray-300'}`}
      onClick={() => setActiveTab('servicios')}
    ></div>
  </div>
</div>
</div>
);
};

export default ConsPublish;